/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { SandboxTiles } from "components/loading-sandbox-list/SandboxTiles";
import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { routeTo } from "router";
import { useAppDispatch } from "store";
import { actionItemsAdded } from "store/features";
import { CodeShareMetadata } from "types";
import { SvgSearch } from "@itwin/itwinui-icons-react";
import { Button, LabeledInput } from "@itwin/itwinui-react";
import { ModalManager } from "../../components/modal/ModalManager";
import { PaginationButton } from "../../components/pagination/PaginationButton";
import { PaginationDirectionButton } from "../../components/pagination/PaginationDirectionButton";
import { PaginationEllipsis } from "../../components/pagination/PaginationEllipsis";
import { closeAllToasts } from "../../components/toasts/Toasts";
import { useComponentEngagement } from "../../hooks/useComponentEngagement";
import { useDataFetching } from "../../hooks/useDataFetching";
import { SearchClient } from "../../services/search-service/SearchClient";
import styles from "./SearchView.module.css";
import { useTrackSandboxEvent } from "services/telemetry-service/telemetry";

export const SearchView: FunctionComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>("");
  const [term, setTerm] = useState<string>();
  const [page, setPage] = useState<number>(0);
  const [searchCodeshares, data, status] = useDataFetching(SearchClient.searchCodeshares);
  const dispatch = useAppDispatch();
  const trackEvent = useTrackSandboxEvent("Search");

  useComponentEngagement("Search");

  useEffect(() => {
    dispatch(actionItemsAdded(["create", "shareIdea"]));

    return () => {
      ModalManager.closeModal();
      closeAllToasts();
    };
  }, [dispatch]);

  useEffect(() => {
    setPage(0);
    const urlParams = new URLSearchParams(location.search);
    const termParameter = urlParams.get("term") || location.hash;
    if (termParameter) {
      const decodedTerm = decodeURIComponent(termParameter);
      setTerm(decodedTerm);
      setSearchValue(decodedTerm);
    }
  }, [location.hash, location.search, searchCodeshares]);

  useEffect(() => {
    if (term) {
      searchCodeshares(term, page);
    }
  }, [page, searchCodeshares, term]);

  useEffect(() => {
    if (term && data) {
      const properties = {
        "Term": term,
        "Pages": data.pages,
        "Current page": data.page,
        "Page sandbox count": data.codeshares.length,
      };
      trackEvent(properties);
    }
  }, [data, term]);

  const onPageClick = useCallback((value: number) => {
    setPage(value - 1);
  }, []);

  const onSearchClick = (event?: React.FormEvent<HTMLFormElement>, value?: string) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!value)
      return;

    navigate(routeTo.search(value));
  }
  let pageNav: ReactNode[] = [];
  if (data) {
    const currentValue = data.page + 1;
    const pagination = paginate(data.pages, currentValue);
    pageNav = pagination.map((num) => {
      if (num > -1) {
        return <PaginationButton value={num} onClick={onPageClick} active={num === currentValue} label={num.toString()} />;
      } else {
        return <PaginationEllipsis />;
      }
    });

    pageNav.unshift(<PaginationDirectionButton value={currentValue - 1} onClick={onPageClick} direction={"left"} disabled={currentValue <= 1} />);
    pageNav.push(<PaginationDirectionButton value={currentValue + 1} onClick={onPageClick} direction={"right"} disabled={currentValue >= data.pages} />);
  }

  return (
    <div className={styles.search} data-testid="atp-search-view">
      <form className={styles.searchBoxContainer} onSubmit={(event) => onSearchClick(event, searchValue)}>
        <LabeledInput
          placeholder="Enter search term..."
          value={searchValue ?? term}
          displayStyle="inline"
          className={styles.searchBox}
          onChange={(event) => setSearchValue(event.target.value)}
          svgIcon={<SvgSearch />}
        />
        <Button styleType="high-visibility" type="submit">Search</Button>
      </form>
      <div className={styles.searchResults}>
        <SandboxTiles list={data?.codeshares as CodeShareMetadata[]} isLoading={status === "fetching"} />
      </div>
      <div className={styles.pagination}>
        {pageNav}
      </div>
    </div>
  );
};

function paginate(
  totalPages: number,
  currentPage: number = 1,
  maxPages: number = 9
) {

  // ensure current page isn't out of range
  if (currentPage < 1) {
    currentPage = 1;
  } else if (currentPage > totalPages) {
    currentPage = totalPages;
  }

  let startPage: number, endPage: number;
  // total pages more than max so calculate start and end pages
  const maxPagesBeforeCurrentPage = Math.floor(maxPages / 2);
  const maxPagesAfterCurrentPage = Math.ceil(maxPages / 2) - 1;
  const nearStart = currentPage <= maxPagesBeforeCurrentPage;
  const nearEnd = currentPage + maxPagesAfterCurrentPage >= totalPages;
  const nearMiddle = !nearStart && !nearEnd;
  if (totalPages <= maxPages) {
    // total pages less than max so show all pages
    startPage = 1;
    endPage = totalPages;
  } else {
    if (nearStart) {
      // current page near the start
      startPage = 1;
      endPage = maxPages;
    } else if (nearEnd) {
      // current page near the end
      startPage = totalPages - maxPages + 1;
      endPage = totalPages;
    } else {
      // current page somewhere in the middle
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  // create an array of pages to ng-repeat in the pager control
  const pages = Array.from(Array((endPage + 1) - startPage).keys()).map((i) => startPage + i);
  if (totalPages > maxPages) {
    if (nearEnd || nearMiddle) {
      pages.splice(0, 2, 1, -1);
    }
    if (nearStart || nearMiddle) {
      pages.splice(maxPages - 2, 2, -1, totalPages);
    }
  }

  // return object with all pager properties required by the view
  return pages;
}

